import { Timestamp } from 'firebase/firestore';
import {
  AccessLevel,
  BLUser,
  IBreathworkMetrics,
  accessLevelFromString,
  loginProviderFromFirebaseId,
  LoginProvider,
  userSettingsFromJson,
} from '@breath-lab/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function blUserFromFirestore(data: Record<string, any>): BLUser {
  return {
    uid: data['uid'] as string,
    // Tolerate missing accessLevel (docs created before the field, or created
    // without it under the rules pin) — default to free.
    accessLevel: data['accessLevel']
      ? accessLevelFromString(data['accessLevel'] as string)
      : AccessLevel.free,
    connectedSignInProviders: (data['connectedSignInProviders'] as string[])
      .map(loginProviderFromFirebaseId)
      .filter((p): p is LoginProvider => p !== null),
    createdAt: (data['createdAt'] as Timestamp).toDate(),
    displayName: (data['displayName'] as string | null) ?? null,
    email: (data['email'] as string | null) ?? null,
    fcmToken: (data['fcmToken'] as string | null) ?? null,
    webFcmToken: (data['webFcmToken'] as string | null) ?? null,
    lastLoginAt: (data['lastLoginAt'] as Timestamp).toDate(),
    lastSeenAt: data['lastSeenAt'] ? (data['lastSeenAt'] as Timestamp).toDate() : null,
    photoRef: (data['photoRef'] as string | null) ?? null,
    reminderTimeUtcMinutesSinceMidnight:
      (data['reminderTimeUtcMinutesSinceMidnight'] as number | null) ?? null,
    termsAndPrivacyAgreedAt:
      ((data['termsAndPrivacyAgreedAt'] as Timestamp[] | null) ?? []).map((t) => t.toDate()),
    timezoneOffsetFromUtc: (data['timezoneOffsetFromUtc'] as number | null) ?? null,
    appVersion: (data['appVersion'] as string | null) ?? null,
    appBuildNumber: (data['appBuildNumber'] as number | null) ?? null,
    webVersion: (data['webVersion'] as string | null) ?? null,
    webBuildNumber: (data['webBuildNumber'] as number | null) ?? null,
    signupPlatform: (data['signupPlatform'] as string | null) ?? null,
    locale: (data['locale'] as string | null) ?? null,
    settings: data['settings'] ? userSettingsFromJson(data['settings'] as Record<string, unknown>) : null,
    breathworkMetrics: (data['breathworkMetrics'] as IBreathworkMetrics | null) ?? null,
    referredBy: (data['referredBy'] as string | null) ?? null,
    onboardedAt: data['onboardedAt'] ? (data['onboardedAt'] as Timestamp).toDate() : null,
    trialStartedAt: data['trialStartedAt'] ? (data['trialStartedAt'] as Timestamp).toDate() : null,
  };
}

// Mobile-only fields (fcmToken, appVersion, appBuildNumber, reminderTimeUtcMinutesSinceMidnight)
// are intentionally omitted — callers use setDoc with merge:true so mobile writes are preserved.
export function blUserToFirestore(user: BLUser): Record<string, unknown> {
  return {
    uid: user.uid,
    // accessLevel is intentionally NOT written: the RevenueCat webhook is the
    // only writer (firestore.rules pins it). A stale in-memory value here
    // would clobber a webhook grant/revoke — and the rules would reject the
    // whole write.
    connectedSignInProviders: user.connectedSignInProviders.map((p) => p as string),
    createdAt: Timestamp.fromDate(user.createdAt),
    displayName: user.displayName,
    email: user.email,
    webFcmToken: user.webFcmToken,
    lastLoginAt: Timestamp.fromDate(user.lastLoginAt),
    lastSeenAt: user.lastSeenAt ? Timestamp.fromDate(user.lastSeenAt) : null,
    photoRef: user.photoRef,
    settings: user.settings,
    termsAndPrivacyAgreedAt: user.termsAndPrivacyAgreedAt.map((d) => Timestamp.fromDate(d)),
    timezoneOffsetFromUtc: user.timezoneOffsetFromUtc,
    webVersion: user.webVersion,
    webBuildNumber: user.webBuildNumber,
    locale: user.locale,
    referredBy: user.referredBy ?? null,
  };
}
